import { Model } from 'app/model/backendmodel';
import { AdapterRegistry } from 'app/model/adapters/adapter-registry';
import { Metamodel } from 'app/model/base/model';

import { Activity } from 'app/model/pipeline/activity';
import { Owned } from 'app/model/base/named';
import { AzurePostgreSQLSink } from 'app/model/pipeline/copysink/azure-postgre-sql.copysink';
import { TabularSinkAdapter } from './tabular.sink.adapter';
import { ValueAdapter } from '../../value.adapter';
import { CopySinkAdapter } from 'app/model/adapters/activities/copysink/copysink.adapter';
import { SharedModel } from 'shell-models-lib';
import { adfModelTypeMap } from 'app/registries/consts/adf-model-type-map-const';

@AdapterRegistry.adapter({
    model: Metamodel.modelFor(AzurePostgreSQLSink),
    asynchronous: true,
    adapts: {
        type: '*',
        writeBatchTimeout: '*',
        writeBatchSize: '*',
        preCopyScript: '*',
        writeMethod: '*',
        upsertSettings: '*'
    }
})
export class AzurePostgreSQLSinkAdapter extends CopySinkAdapter<AzurePostgreSQLSink, Model.AzurePostgreSQLSink> {
    protected override async _toResource(model: AzurePostgreSQLSink): Promise<Model.AzurePostgreSQLSink> {
        const result: Model.AzurePostgreSQLSink = {
            type: model.nullableOriginalType === 'AzurePostgreSQLSink' ?
                'AzurePostgreSQLSink' :
                adfModelTypeMap.azurePostgreSQL.copySinkType
        };
        TabularSinkAdapter.toSinkResource(model, result);
        result.writeMethod = ValueAdapter.toResource(model, AzurePostgreSQLSink.WRITE_METHOD) || undefined;
        if (result.writeMethod === 'Upsert') {
            this._upsertToResource(model, result);
        }
        return result;
    }

    protected override async _toModel(
        resource: Model.AzurePostgreSQLSink,
        owner: Activity
    ): Promise<AzurePostgreSQLSink> {
        const result = Owned.create<AzurePostgreSQLSink>(Metamodel.modelFor(AzurePostgreSQLSink), owner);
        TabularSinkAdapter.toSinkModel(resource, result);
        result.writeMethod = undefined;
        ValueAdapter.toModel(result, AzurePostgreSQLSink.WRITE_METHOD, resource.writeMethod);
        if (resource.writeMethod === 'Upsert') {
            this._upsertToModel(resource, result);
        }

        // previously it was typed `AzurePostgreSQLSink`, thus we colored it upon nullableOriginalType
        // an its to_resource could get, it should use the old name for compatibility.
        if (resource.type === 'AzurePostgreSQLSink') {
            result.nullableOriginalType = resource.type;
        }
        return result;
    }

    private _upsertToResource(model: AzurePostgreSQLSink, result: Model.AzurePostgreSQLSink): void {
        // for copy-wizard generated activities, where upsertSettings parameterized as one
        const upsertSettings = ValueAdapter.toResource(model, AzurePostgreSQLSink.UPSERT_SETTINGS);
        if (upsertSettings?.type === 'Expression') {
            result.upsertSettings = upsertSettings;
        } else {
            result.upsertSettings = {
                keys: ValueAdapter.toResource(model, AzurePostgreSQLSink.WRITE_BEHAVIOR_KEYS)
            };
        }
    }

    private _upsertToModel(resource: Model.AzurePostgreSQLSink, result: AzurePostgreSQLSink): void {
        if (resource.upsertSettings) {
            // for copy-wizard generated activities, where upsertSettings parameterized as one
            if ((resource.upsertSettings as SharedModel.ExpressionValue)?.type === 'Expression') {
                ValueAdapter.toModel(result, AzurePostgreSQLSink.UPSERT_SETTINGS, resource.upsertSettings);
            } else {
                const upsertSettings = resource.upsertSettings as Model.WriteBehaviorUpsertOptions;
                ValueAdapter.toModel(result, AzurePostgreSQLSink.WRITE_BEHAVIOR_KEYS, upsertSettings.keys);
            }
        }
    }
}
